import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';
import Alert from 'kingpin/Alert';

import Label from '../Label';
import Inputs from 'components/Inputs';
import Row from 'components/Common/Row';
import Tooltip from 'components/Tooltip';
import useMinYTypeOptions from './useMinYTypeOptions';
import useMaxYTypeOptions from './useMaxYTypeOptions';

const InputWrapper = styled.div`
  margin-bottom: 8px;
`;

const MIN_RANGE_PRESETS: Array<{
  label: string;
  description: string;
  value: number | undefined;
}> = [
  {
    label: 'None',
    description:
      'Axis zooms to fit your data exactly. Small variations can appear as dramatic spikes.',
    value: undefined,
  },
  {
    label: 'Subtle',
    description:
      'Has a noticeable effect only when values are tightly clustered. If your data varies by less than ~25% of its peak value, this will widen the axis enough to put it in context.',
    value: 40,
  },
  {
    label: 'Moderate',
    description:
      'Effective for most data. Widens the axis enough to smooth out spikes without losing too much detail.',
    value: 50,
  },
  {
    label: 'Wide',
    description:
      'Significantly widens the axis regardless of how much your data varies. Changes will appear flatter and more gradual.',
    value: 80,
  },
];

const YAxisConfig = ({
  series,
  updateSeries,
}: {
  series: V5ChartDefinitionSeries;
  updateSeries: (s: V5ChartDefinitionSeries) => void;
}) => {
  const [minYType, setMinYType] = useState<YAxisConfigType>(
    series.minY === undefined ? 'Auto' : 'Set',
  );

  const [maxYType, setMaxYType] = useState<YAxisConfigType>(
    series.maxY === undefined ? 'Auto' : 'Set',
  );

  const onMinYValueChanged = useCallback(
    (newValue?: number) => {
      if (minYType === 'Set') {
        updateSeries({ ...series, minY: newValue });
      }
    },
    [minYType, series, updateSeries],
  );

  const onMaxYValueChanged = useCallback(
    (newValue?: number) => {
      if (maxYType === 'Set') {
        updateSeries({ ...series, maxY: newValue });
      }
    },
    [maxYType, series, updateSeries],
  );

  const onMinYTypeChanged = useCallback(
    (newType: YAxisConfigType) => {
      setMinYType(newType);
      if (newType === 'Auto' && series.minY !== undefined) {
        updateSeries({ ...series, minY: undefined });
      }
    },
    [series, updateSeries],
  );

  const onMaxYTypeChanged = useCallback(
    (newType: YAxisConfigType) => {
      setMaxYType(newType);
      if (newType === 'Auto' && series.maxY !== undefined) {
        updateSeries({ ...series, maxY: undefined });
      }
    },
    [series, updateSeries],
  );

  const minYTypeOptions = useMinYTypeOptions({ minYType, onMinYTypeChanged });
  const maxYTypeOptions = useMaxYTypeOptions({ maxYType, onMaxYTypeChanged });

  const minRangeOptions = useMemo(
    () =>
      MIN_RANGE_PRESETS.map((preset) => ({
        label: preset.label,
        isSelected: series.minRangePercent === preset.value,
        onSelected: () =>
          updateSeries({ ...series, minRangePercent: preset.value }),
        renderLabel: () => (
          <div>
            <div>
              <Typography.Body type="Label">{preset.label}</Typography.Body>
            </div>
            <div>
              <Typography.Body type="Label" color="var(--color-grey-4)">
                {preset.description}
              </Typography.Body>
            </div>
          </div>
        ),
      })),
    [series, updateSeries],
  );

  return (
    <>
      <InputWrapper>
        <Label>Min Y behaviour</Label>
        <Inputs.Dropdown options={minYTypeOptions} />
      </InputWrapper>

      {minYType === 'Set' && (
        <InputWrapper>
          <Label>Set Min Y:</Label>
          <Inputs.Number
            value={series.minY}
            setValue={onMinYValueChanged}
            placeholder="0"
          />
        </InputWrapper>
      )}

      <InputWrapper>
        <Label>Max Y behaviour</Label>
        <Inputs.Dropdown options={maxYTypeOptions} />
      </InputWrapper>

      {maxYType === 'Set' && (
        <InputWrapper>
          <Label>Set Max Y:</Label>
          <Inputs.Number
            value={series.maxY}
            setValue={onMaxYValueChanged}
            placeholder="0"
          />
        </InputWrapper>
      )}

      <InputWrapper>
        <Row centerAlign style={{ gap: 4, marginBottom: 4 }}>
          <Typography.Body type="Label">Min visible range</Typography.Body>
          <Tooltip content="Without this setting, small variations can look like dramatic spikes because the Y-axis zooms to fit your data. Setting a minimum range forces the axis to always show a wider view, putting changes in context.">
            <Icon icon="info" color="var(--color-grey-3)" />
          </Tooltip>
        </Row>
        <Inputs.Dropdown options={minRangeOptions} />
      </InputWrapper>

      {series.minY !== undefined &&
        series.maxY !== undefined &&
        series.minY >= series.maxY && (
          <Alert
            title={'Invalid Axis configured'}
            body={
              'You have configured the minimum value of this axis to be greater than the maximum value. Please revise your settings to continue.'
            }
            type={'Danger'}
          />
        )}
    </>
  );
};

export default YAxisConfig;
